import { useMemo, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

import type { OrganizationDto } from '../api/types'
import type { MessengerBridge } from '../bridge'
import { usePwaInstall } from '../hooks/usePwaInstall'
import { safeDisplayText } from '../utils/pdnDisplay'

type ProfileEntitlements = {
  planDisplayName: string
  periodEnd: string | null
  isStartPlan: boolean
  isPremium: boolean
}

type ProfileScreenProps = {
  bridge: MessengerBridge
  displayName: string
  email: string
  organizations: OrganizationDto[]
  activeOrganizationId: number
  onSelectOrganization: (id: number) => void
  entitlements: ProfileEntitlements
  onBack: () => void
  /** Реальный выход из аккаунта (standalone PWA). Нет → фолбэк bridge.close(). */
  onLogout?: () => void
  embedded?: boolean
}

const MARKET_URL = 'https://market.provisium.ru'
const SUPPORT_EMAIL = 'support@provisium.ru'
const SUPPORT_ATTEMPTS_KEY = 'provisium.miniapp.support.attempts'

export type SupportMailContext = {
  question: string
  displayName: string
  email: string
  organizationName: string
  organizationId: number
  planLabel: string
}

/** Compose mailto for support — body is ready to send as-is. */
export const buildSupportMailtoUrl = ({
  question,
  displayName,
  email,
  organizationName,
  organizationId,
  planLabel,
}: SupportMailContext): string => {
  const subject = 'Обращение из миниаппа Provisium'
  const userEmail = email.trim() || 'не указан'
  const body = [
    'Здравствуйте!',
    '',
    'Просьба обратить внимание на обращение пользователя из приложения «Провизиум» и ответить ему.',
    '',
    'Данные пользователя:',
    `• Имя: ${displayName}`,
    `• Email: ${userEmail}`,
    `• Организация: ${organizationName} (ID ${organizationId})`,
    `• Тариф: ${planLabel}`,
    '',
    'Ситуация / вопрос пользователя:',
    question.trim(),
    '',
    'Пожалуйста, разберитесь в ситуации и напишите пользователю ответ на его email (если указан) либо через доступный канал связи.',
    '',
    '—',
    'Письмо сформировано автоматически из приложения «Провизиум»',
  ].join('\n')

  return `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`
}

const openMailto = (mailtoUrl: string) => {
  // Telegram openLink accepts only http(s); mailto must go through a native anchor.
  const anchor = document.createElement('a')
  anchor.href = mailtoUrl
  anchor.rel = 'noopener noreferrer'
  anchor.style.display = 'none'
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
}

const formatPeriodEnd = (iso: string | null): string | null => {
  if (!iso) {
    return null
  }
  return new Intl.DateTimeFormat('ru-RU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(new Date(iso))
}

const orgDisplayName = (org: OrganizationDto): string =>
  safeDisplayText(org.trade_name, '') ||
  safeDisplayText(org.legal_name, `Организация #${org.id}`)

const roleLabel = (role: string | null | undefined): string => {
  switch (role) {
    case 'owner':
      return 'Владелец'
    case 'admin':
      return 'Администратор'
    case 'manager':
      return 'Менеджер'
    case 'member':
      return 'Сотрудник'
    default:
      return role ? role : '—'
  }
}

const initials = (name: string): string => {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) {
    return '?'
  }
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase()
  }
  return `${parts[0][0] ?? ''}${parts[1][0] ?? ''}`.toUpperCase()
}

const storeSupportAttempt = (payload: {
  question: string
  organizationId: number
  at: string
}) => {
  try {
    const raw = localStorage.getItem(SUPPORT_ATTEMPTS_KEY)
    const list = raw ? (JSON.parse(raw) as unknown[]) : []
    list.push(payload)
    localStorage.setItem(SUPPORT_ATTEMPTS_KEY, JSON.stringify(list.slice(-20)))
  } catch {
    // best effort
  }
}

const openExternal = (url: string, bridge: MessengerBridge) => {
  if (url.startsWith('https://t.me/') && bridge.openTelegramLink) {
    bridge.openTelegramLink(url)
    return
  }
  if (bridge.openLink) {
    bridge.openLink(url)
    return
  }
  window.open(url, '_blank', 'noopener,noreferrer')
}

const RowIcon = ({ children }: { children: ReactNode }) => (
  <span className="rp-set-row__ico" aria-hidden="true">
    {children}
  </span>
)

export const ProfileScreen = ({
  bridge,
  displayName,
  email,
  organizations,
  activeOrganizationId,
  onSelectOrganization,
  entitlements,
  onBack,
  onLogout,
  embedded = false,
}: ProfileScreenProps) => {
  const { canInstall, installed, promptInstall } = usePwaInstall()
  const [question, setQuestion] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [submitMessage, setSubmitMessage] = useState<string | null>(null)
  const [orgPickerOpen, setOrgPickerOpen] = useState(false)
  const [helpOpen, setHelpOpen] = useState(false)
  const [cabinetOpen, setCabinetOpen] = useState(false)
  const [tariffOpen, setTariffOpen] = useState(false)
  const [installOpen, setInstallOpen] = useState(false)

  const buyerOrganizations = useMemo(() => {
    const buyers = organizations.filter((org) => org.org_type === 'buyer')
    return buyers.length > 0 ? buyers : organizations
  }, [organizations])

  const activeOrganization = useMemo(
    () => buyerOrganizations.find((org) => org.id === activeOrganizationId) ?? buyerOrganizations[0],
    [activeOrganizationId, buyerOrganizations],
  )

  const visibleName = safeDisplayText(displayName, 'Пользователь')
  const visibleEmail = safeDisplayText(email, '')
  const visibleOrg = activeOrganization ? orgDisplayName(activeOrganization) : '—'
  const periodLabel = formatPeriodEnd(entitlements.periodEnd)
  const tariffSub = entitlements.isPremium
    ? periodLabel
      ? `Действует до ${periodLabel}`
      : 'Premium'
    : 'Подключите Premium для аналитики цен'
  const tariffVal = entitlements.isPremium
    ? entitlements.planDisplayName
    : 'Старт'

  const handleOpenMarket = () => {
    // Close profile first so Telegram WebView doesn't reflow the sheet under the browser.
    onBack()
    window.setTimeout(() => openExternal(MARKET_URL, bridge), 50)
  }

  const handleOpenHelp = () => {
    setOrgPickerOpen(false)
    setHelpOpen(true)
  }

  const handleSubmitQuestion = () => {
    const trimmed = question.trim()
    if (!trimmed || submitting) {
      return
    }

    setSubmitting(true)
    setSubmitMessage(null)
    storeSupportAttempt({
      question: trimmed,
      organizationId: activeOrganizationId,
      at: new Date().toISOString(),
    })

    const mailtoUrl = buildSupportMailtoUrl({
      question: trimmed,
      displayName: visibleName,
      email: visibleEmail,
      organizationName: visibleOrg,
      organizationId: activeOrganizationId,
      planLabel: tariffVal,
    })

    try {
      openMailto(mailtoUrl)
      setSubmitMessage(
        `Открыли письмо на ${SUPPORT_EMAIL}. Проверьте почтовое приложение и нажмите «Отправить».`,
      )
      setQuestion('')
    } catch {
      setSubmitMessage(
        `Не удалось открыть почту. Напишите на ${SUPPORT_EMAIL} и опишите ситуацию вручную.`,
      )
    } finally {
      setSubmitting(false)
    }
  }

  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : ''
  const isIosDevice = /iphone|ipad|ipod/i.test(ua)
  const isAndroidDevice = /android/i.test(ua)

  const portalTarget = typeof document !== 'undefined' ? document.body : null

  const orgSheet =
    orgPickerOpen && portalTarget
      ? createPortal(
          <div className="rp-sheet rp-sheet--nested" role="dialog" aria-modal="true" aria-labelledby="org-sheet-title">
            <button
              type="button"
              className="rp-sheet__scrim"
              aria-label="Закрыть выбор заведения"
              onClick={() => setOrgPickerOpen(false)}
            />
            <div className="rp-sheet__panel">
              <div className="rp-sheet__grip" aria-hidden="true" />
              <div className="rp-sheet__head">
                <h3 id="org-sheet-title" className="rp-sheet__title">Выбор заведения</h3>
                <p className="rp-sheet__sub">Переключение между вашими организациями</p>
              </div>
              <div className="rp-sheet__body">
                <div className="rp-set-group" role="listbox" aria-label="Организации">
                  {buyerOrganizations.map((org) => {
                    const selected = org.id === activeOrganizationId
                    const label = orgDisplayName(org)
                    return (
                      <button
                        key={org.id}
                        type="button"
                        role="option"
                        aria-selected={selected}
                        className={selected ? 'rp-set-row rp-set-row--active' : 'rp-set-row'}
                        onClick={() => {
                          onSelectOrganization(org.id)
                          setOrgPickerOpen(false)
                        }}
                      >
                        <span className="rp-set-row__body">
                          <span className="rp-set-row__title">{label}</span>
                          <span className="rp-set-row__sub">
                            {safeDisplayText(org.legal_name, `ID ${org.id}`)}
                          </span>
                        </span>
                        <span className="rp-set-row__val" aria-hidden={!selected}>
                          {selected ? '✓' : ''}
                        </span>
                      </button>
                    )
                  })}
                </div>
              </div>
              <div className="rp-sheet__foot">
                <button
                  type="button"
                  className="echat-btn echat-btn--ghost"
                  onClick={() => setOrgPickerOpen(false)}
                >
                  Закрыть
                </button>
              </div>
            </div>
          </div>,
          portalTarget,
        )
      : null

  const helpSheet =
    helpOpen && portalTarget
      ? createPortal(
          <div className="rp-sheet rp-sheet--nested" role="dialog" aria-modal="true" aria-labelledby="support-sheet-title">
            <button
              type="button"
              className="rp-sheet__scrim"
              aria-label="Закрыть поддержку"
              onClick={() => setHelpOpen(false)}
            />
            <div className="rp-sheet__panel">
              <div className="rp-sheet__grip" aria-hidden="true" />
              <div className="rp-sheet__head">
                <h3 id="support-sheet-title" className="rp-sheet__title">Задать вопрос</h3>
                <p className="rp-sheet__sub">На {SUPPORT_EMAIL}</p>
              </div>
              <div className="rp-sheet__body">
                <label className="echat-field">
                  <span className="echat-field__label">Опишите проблему</span>
                  <textarea
                    className="echat-field__input"
                    rows={4}
                    value={question}
                    placeholder="Что произошло и что нужно помочь решить"
                    onChange={(event) => setQuestion(event.target.value)}
                  />
                </label>
                <p className="rp-set-hint rp-set-hint--left">
                  Откроется письмо на {SUPPORT_EMAIL} с вашим текстом и данными аккаунта.
                  Ответ придёт на вашу почту.
                </p>
                {submitMessage ? <p className="echat-panel__meta">{submitMessage}</p> : null}
              </div>
              <div className="rp-sheet__foot rp-sheet__foot--split">
                <button
                  type="button"
                  className="echat-btn echat-btn--ghost"
                  onClick={() => setHelpOpen(false)}
                >
                  Отмена
                </button>
                <button
                  type="button"
                  className="echat-btn echat-btn--primary"
                  disabled={submitting || !question.trim()}
                  onClick={handleSubmitQuestion}
                >
                  {submitting ? 'Открытие…' : 'Написать в поддержку'}
                </button>
              </div>
            </div>
          </div>,
          portalTarget,
        )
      : null

  const cabinetSheet =
    cabinetOpen && portalTarget
      ? createPortal(
          <div className="rp-sheet rp-sheet--nested" role="dialog" aria-modal="true" aria-labelledby="cabinet-sheet-title">
            <button
              type="button"
              className="rp-sheet__scrim"
              aria-label="Закрыть кабинет"
              onClick={() => setCabinetOpen(false)}
            />
            <div className="rp-sheet__panel">
              <div className="rp-sheet__grip" aria-hidden="true" />
              <div className="rp-sheet__head">
                <h3 id="cabinet-sheet-title" className="rp-sheet__title">Личный кабинет</h3>
                <p className="rp-sheet__sub">Данные аккаунта и организации</p>
              </div>
              <div className="rp-sheet__body">
                <div className="rp-cab-group">
                  <p className="rp-cab-group__title">Аккаунт</p>
                  <dl className="rp-cab-list">
                    <div className="rp-cab-row">
                      <dt>Имя</dt>
                      <dd>{visibleName}</dd>
                    </div>
                    {visibleEmail ? (
                      <div className="rp-cab-row">
                        <dt>Email</dt>
                        <dd>{visibleEmail}</dd>
                      </div>
                    ) : null}
                    <div className="rp-cab-row">
                      <dt>Роль</dt>
                      <dd>{roleLabel(activeOrganization?.member_role)}</dd>
                    </div>
                  </dl>
                </div>

                <div className="rp-cab-group">
                  <p className="rp-cab-group__title">Организация</p>
                  <dl className="rp-cab-list">
                    <div className="rp-cab-row">
                      <dt>Название</dt>
                      <dd>{visibleOrg}</dd>
                    </div>
                    <div className="rp-cab-row">
                      <dt>Тариф</dt>
                      <dd>{tariffVal}</dd>
                    </div>
                  </dl>
                </div>

                <div className="rp-cab-group">
                  <p className="rp-cab-group__title">Реквизиты</p>
                  <dl className="rp-cab-list">
                    <div className="rp-cab-row">
                      <dt>Юрлицо</dt>
                      <dd>{safeDisplayText(activeOrganization?.legal_name, '—')}</dd>
                    </div>
                    {activeOrganization?.inn ? (
                      <div className="rp-cab-row">
                        <dt>ИНН</dt>
                        <dd>{safeDisplayText(activeOrganization.inn, '—')}</dd>
                      </div>
                    ) : null}
                  </dl>
                  <p className="rp-set-hint rp-set-hint--left">
                    Смена реквизитов — в личном кабинете на сайте.
                  </p>
                </div>
              </div>
              <div className="rp-sheet__foot">
                <button
                  type="button"
                  className="echat-btn echat-btn--ghost"
                  onClick={() => setCabinetOpen(false)}
                >
                  Закрыть
                </button>
              </div>
            </div>
          </div>,
          portalTarget,
        )
      : null

  const tariffSheet =
    tariffOpen && portalTarget
      ? createPortal(
          <div className="rp-sheet rp-sheet--nested" role="dialog" aria-modal="true" aria-labelledby="tariff-sheet-title">
            <button
              type="button"
              className="rp-sheet__scrim"
              aria-label="Закрыть тариф"
              onClick={() => setTariffOpen(false)}
            />
            <div className="rp-sheet__panel">
              <div className="rp-sheet__grip" aria-hidden="true" />
              <div className="rp-sheet__head">
                <h3 id="tariff-sheet-title" className="rp-sheet__title">Тариф</h3>
                <p className="rp-sheet__sub">{tariffSub}</p>
              </div>
              <div className="rp-sheet__body">
                <div className="rp-cab-list">
                  <div className="rp-cab-row">
                    <dt>Текущий тариф</dt>
                    <dd>{tariffVal}</dd>
                  </div>
                  {periodLabel ? (
                    <div className="rp-cab-row">
                      <dt>Действует до</dt>
                      <dd>{periodLabel}</dd>
                    </div>
                  ) : null}
                </div>

                <p className="rp-set-hint rp-set-hint--left">
                  {entitlements.isPremium
                    ? 'Premium открывает аналитику цен и расширенные лимиты. Управление подпиской — на сайте.'
                    : 'Тариф «Старт» — базовые функции. Premium добавляет аналитику цен и расширенные лимиты.'}
                </p>
              </div>
              <div className="rp-sheet__foot rp-sheet__foot--split">
                <button
                  type="button"
                  className="echat-btn echat-btn--ghost"
                  onClick={() => setTariffOpen(false)}
                >
                  Закрыть
                </button>
                <button
                  type="button"
                  className="echat-btn echat-btn--primary"
                  onClick={handleOpenMarket}
                >
                  {entitlements.isPremium ? 'Управление подпиской →' : 'Подключить Premium →'}
                </button>
              </div>
            </div>
          </div>,
          portalTarget,
        )
      : null

  const installSheet =
    installOpen && portalTarget
      ? createPortal(
          <div className="rp-sheet rp-sheet--nested" role="dialog" aria-modal="true" aria-labelledby="install-sheet-title">
            <button
              type="button"
              className="rp-sheet__scrim"
              aria-label="Закрыть установку"
              onClick={() => setInstallOpen(false)}
            />
            <div className="rp-sheet__panel">
              <div className="rp-sheet__grip" aria-hidden="true" />
              <div className="rp-sheet__head">
                <h3 id="install-sheet-title" className="rp-sheet__title">На главный экран</h3>
                <p className="rp-sheet__sub">Ярлык «Провизиум» — как обычное приложение</p>
              </div>
              <div className="rp-sheet__body">
                {installed ? (
                  <p className="pwa-auth__message" role="status">
                    Приложение уже установлено на этом устройстве.
                  </p>
                ) : (
                  <>
                    {canInstall ? (
                      <button
                        type="button"
                        className="echat-btn echat-btn--primary"
                        onClick={() => {
                          void promptInstall()
                          setInstallOpen(false)
                        }}
                      >
                        Установить приложение
                      </button>
                    ) : null}

                    <ol className="rp-install-steps">
                      {isIosDevice ? (
                        <li>
                          <b>iPhone / iPad (Safari):</b> кнопка «Поделиться» внизу → «На экран „Домой“».
                        </li>
                      ) : isAndroidDevice ? (
                        <li>
                          <b>Android (Chrome):</b> меню «⋮» справа вверху → «Установить приложение» / «Добавить на главный экран».
                        </li>
                      ) : (
                        <li>
                          <b>Компьютер:</b> значок установки в адресной строке, либо меню браузера → «Установить приложение».
                        </li>
                      )}
                      {!canInstall && !isIosDevice ? (
                        <li>
                          В другом браузере ищите пункт «Установить приложение» или «Добавить на главный экран» в его меню.
                        </li>
                      ) : null}
                    </ol>
                  </>
                )}
              </div>
              <div className="rp-sheet__foot">
                <button
                  type="button"
                  className="echat-btn echat-btn--ghost"
                  onClick={() => setInstallOpen(false)}
                >
                  Закрыть
                </button>
              </div>
            </div>
          </div>,
          portalTarget,
        )
      : null

  return (
    <section className={`rp-screen rp-profile${embedded ? ' rp-profile--embedded' : ''}`} aria-label="Профиль и настройки">
      {!embedded ? (
        <>
          <button type="button" className="echat-back" onClick={onBack}>
            ← Назад
          </button>
          <h2 className="rp-profile__title">Профиль и настройки</h2>
        </>
      ) : null}

      <div className="rp-prof-card">
        <span className="rp-prof-ava" aria-hidden="true">
          {initials(visibleName)}
        </span>
        <div className="rp-prof-card__text">
          <p className="rp-prof-name">{visibleName}</p>
          {visibleEmail ? <p className="rp-prof-org">{visibleEmail}</p> : null}
          <p className="rp-prof-org">{visibleOrg}</p>
        </div>
      </div>

      <div className="rp-set-group" role="group" aria-label="Организация и тариф">
        <button
          type="button"
          className="rp-set-row"
          aria-expanded={orgPickerOpen}
          onClick={() => {
            setHelpOpen(false)
            setOrgPickerOpen(true)
          }}
        >
          <RowIcon>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 21s7-6 7-11a7 7 0 0 0-14 0c0 5 7 11 7 11z" />
              <circle cx="12" cy="10" r="2.4" />
            </svg>
          </RowIcon>
          <span className="rp-set-row__body">
            <span className="rp-set-row__title">Заведение</span>
            <span className="rp-set-row__sub">Организация / юрлицо</span>
          </span>
          <span className="rp-set-row__val">
            <span className="rp-set-row__val-text">{visibleOrg}</span>
            <span className="rp-set-row__chev" aria-hidden="true">
              ›
            </span>
          </span>
        </button>

        <button
          type="button"
          className="rp-set-row"
          aria-expanded={tariffOpen}
          onClick={() => {
            setOrgPickerOpen(false)
            setHelpOpen(false)
            setCabinetOpen(false)
            setTariffOpen(true)
          }}
        >
          <RowIcon>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 2 4 6v6c0 5 3.5 8 8 10 4.5-2 8-5 8-10V6z" />
            </svg>
          </RowIcon>
          <span className="rp-set-row__body">
            <span className="rp-set-row__title">Тариф</span>
            <span className="rp-set-row__sub">{tariffSub}</span>
          </span>
          <span
            className={
              entitlements.isPremium
                ? 'rp-tariff-badge rp-tariff-badge--premium'
                : 'rp-tariff-badge rp-tariff-badge--start'
            }
          >
            {tariffVal}
          </span>
          <span className="rp-set-row__chev" aria-hidden="true">
            ›
          </span>
        </button>
      </div>

      <div className="rp-set-group" role="group" aria-label="Кабинет и поддержка">
        <button
          type="button"
          className="rp-set-row"
          aria-expanded={cabinetOpen}
          onClick={() => {
            setOrgPickerOpen(false)
            setHelpOpen(false)
            setCabinetOpen(true)
          }}
        >
          <RowIcon>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 5h16v14H4z" />
              <path d="M4 9h16" />
              <path d="M8 13h5" />
            </svg>
          </RowIcon>
          <span className="rp-set-row__body">
            <span className="rp-set-row__title">Личный кабинет</span>
            <span className="rp-set-row__sub">Аккаунт, организация, тариф</span>
          </span>
          <span className="rp-set-row__chev" aria-hidden="true">
            ›
          </span>
        </button>

        <button
          type="button"
          className="rp-set-row"
          aria-expanded={helpOpen}
          onClick={handleOpenHelp}
        >
          <RowIcon>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="9" />
              <path d="M9.5 9a2.5 2.5 0 1 1 3.5 2.3c-.8.4-1 .9-1 1.7" />
              <path d="M12 17h.01" />
            </svg>
          </RowIcon>
          <span className="rp-set-row__body">
            <span className="rp-set-row__title">Помощь и поддержка</span>
            <span className="rp-set-row__sub">Письмо на {SUPPORT_EMAIL}</span>
          </span>
          <span className="rp-set-row__chev" aria-hidden="true">
            ›
          </span>
        </button>
      </div>

      <div className="rp-set-group" role="group" aria-label="Система">
        <button
          type="button"
          className="rp-set-row"
          aria-expanded={installOpen}
          onClick={() => {
            setOrgPickerOpen(false)
            setHelpOpen(false)
            setCabinetOpen(false)
            setInstallOpen(true)
          }}
        >
          <RowIcon>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 11l9-8 9 8" />
              <path d="M5 10v10h14V10" />
            </svg>
          </RowIcon>
          <span className="rp-set-row__body">
            <span className="rp-set-row__title">На главный экран</span>
            <span className="rp-set-row__sub">
              {installed ? 'Приложение установлено' : 'Ярлык приложения на телефоне'}
            </span>
          </span>
          <span className="rp-set-row__chev" aria-hidden="true">
            ›
          </span>
        </button>

        <button
          type="button"
          className="rp-set-row rp-set-row--danger"
          onClick={() => (onLogout ? onLogout() : bridge.close())}
        >
          <RowIcon>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M15 12H4" />
              <path d="m8 8-4 4 4 4" />
              <path d="M14 4h4a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-4" />
            </svg>
          </RowIcon>
          <span className="rp-set-row__body">
            <span className="rp-set-row__title">Выйти из приложения</span>
          </span>
        </button>
      </div>

      <p className="rp-set-hint" aria-live="polite">
        Отвязка аккаунта и смена реквизитов — в личном кабинете на сайте
      </p>

      {orgSheet}
      {helpSheet}
      {cabinetSheet}
      {tariffSheet}
      {installSheet}
    </section>
  )
}
